import { Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { createRef, Fragment, PureComponent } from "react";
import { PopoverBody } from "reactstrap";

import { default as keyConstants } from "../../constants/keyConstants";
import { PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";
import { withContainerContext } from "../container/ContainerContext";
import Icon from "../interface/Icon";
import ListView from "../listView/ListView";
import { withModalSizeContext } from "../modal/ModalSizeContext";
import NavigationToolbar from "../navigationToolbar";
import { popoverOrPortal } from "../portal/popoverOrPortal";
import { withScreenContext } from "../screen/ScreenContext";
import FormSearchField from "./FormSearchField";
import { getDropdownOptions, getFilteredOptions } from "./utils";

const SAFE_SPACE = "__SPACE__";
const SAFE_NULL = "__NULL__";

const isIos = preprocessorQuery.isIos();
const isMobile = preprocessorQuery.isMobile();
const isTablet = preprocessorQuery.isTablet();

export const renderOptionIcon = opt => {
  if (opt.icon) {
    return <Icon icon={opt.icon} color={opt.color} />;
  } else if (opt.color) {
    return <div className={`dropdownColor ${opt.color}`} />;
  } else {
    return null;
  }
};

class FormDropdownMenu extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = { options: null, searchText: "", highlightValue: null, showingSubMenu: null };
    this._listViewDom = null;
    this._domNode = createRef();
    this.showMenu = this.showMenu.bind(this);
    this.hideMenu = this.hideMenu.bind(this);
    this.onToggleSubMenu = this.onToggleSubMenu.bind(this);
    this.hideSubMenu = this.hideSubMenu.bind(this);
    this.searchChanged = this.searchChanged.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDownArrow = this.onDownArrow.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.toggle = this.toggle.bind(this);
    this.optionRenderer = this.optionRenderer.bind(this);
    this.onSubOptionClick = this.onSubOptionClick.bind(this);

    this._subMenuDomsMap = Map();
    this._dropdownId = "x" + uniqueId();
    this._searchRef = createRef();
  }

  componentWillReceiveProps(nextProps) {
    const options = nextProps.showMenu ? getDropdownOptions(nextProps.options) : null;
    this.setState({ searchText: "", options });
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.showMenu && this.props.showMenu && this._listViewDom) {
      if (this.props.getDom) {
        this.props.getDom(this._listViewDom);
      }
      // Note: the auto focusing is used for the keyboard navigation in desktop mode.
      // For iOS mode, the dropdown menu is a portal.
      // Auto focusing would cause the sliding portal not working properly (empty screen)
      // in Safari ie. iOS mode.
      // See https://bitbucket.org/aeontimeline/aeon-timeline-react/pull-requests/557#comment-210576484
      if (!isIos && this.props.focusOnOpen) {
        // Focus the popover when open
        this._listViewDom.focus();
      }
    }
  }

  componentWillUnmount() {
    this.hideSubMenu();
    this.hideMenu();
  }

  toggle() {
    if (this.props.showMenu) {
      this.hideMenu();
    } else {
      this.showMenu();
    }
  }

  getListItemDomId(opt) {
    const val = opt.value ? `${opt.value.toString()}`.replace(/\s/g, SAFE_SPACE) : SAFE_NULL;
    return `${this._dropdownId}-${val}`;
  }

  onDownArrow() {
    this._listViewDom ? this._listViewDom.focus() : null;
    this.setState({ highlightIndex: 0 });
  }

  onKeyDown(e) {
    if (this.props.showMenu) {
      if (e.key === keyConstants.Down) {
        e.preventDefault();
        e.stopPropagation();
        this._listViewDom ? this._listViewDom.focus() : null;
        this.setState({ highlightIndex: 0 });
      } else if ((e.key === keyConstants.Right || e.key === keyConstants.Left) && this.state.showingSubMenu) {
        e.preventDefault();
        e.stopPropagation();
        // Press right should focus the sub menu
        if (e.key === keyConstants.Right) {
          const subMenuDom = this._subMenuDomsMap.get(this.state.showingSubMenu);
          if (subMenuDom) {
            subMenuDom.focus();
          }
        }
        // Press left should focus self
        else {
          this._listViewDom.focus();
        }
      } else {
        this._searchRef && this._searchRef.current ? this._searchRef.current.focus() : null;
      }
    }
  }

  onKeyPress(_e) {
    // TODO: When the user types a key, we should jump directly to that character...?
  }

  showMenu() {
    const options = getDropdownOptions(this.props.options);
    this.setState({
      highlightValue: this.props.value,
      options
    });
    this.props.onToggleMenu(true);
  }

  hideMenu() {
    this.setState({ searchText: "", options: null, showingSubMenu: null });
    this.props.onToggleMenu(false);
  }

  searchChanged(searchText) {
    const filteredOptions = getFilteredOptions(this.state.options, searchText);
    const highlightIndex = _.findIndex(filteredOptions, opt => opt.value === this.state.highlightValue);
    if (highlightIndex < 0) {
      this.setState({ searchText, highlightValue: null });
    }
    this.setState({ searchText });
  }

  renderMenuSearch() {
    if (this.props.allowSearch) {
      return (
        <FormSearchField
          //#!if iOS !== true
          autoFocus={true}
          //#!endif
          ref={this._searchRef}
          placeholder="Filter"
          value={this.state.searchText}
          onChange={this.searchChanged}
          onDownArrow={this.onDownArrow}
          continuouslyUpdates={true}
          showSearchIcon={true}
          showClearIcon={true}
        />
      );
    }
    return null;
  }

  renderNormalOption(opt, isHighlighted, ref) {
    let style = {};
    if (isMobile) {
      style.paddingLeft = Math.max(this.props.paddingLeft, 5);
      style.paddingRight = Math.max(this.props.paddingRight, 5);
    }
    const selected = this.props.value === opt.value;
    const optClass = `dropdownMenuOption ${selected ? "selected" : ""} ${isHighlighted ? "highlighted" : ""} ${
      opt.disabled ? "disabled" : ""
    } ${this.props.showDescription ? "showDescription" : ""}`;
    return (
      <div
        ref={ref}
        className={optClass}
        style={style}
        key={opt.value}
        id={this.getListItemDomId(opt)}
        onClick={() => this.onOptionClick(opt)}
        onMouseEnter={() => this.onMouseInOption(opt)}
      >
        {this.props.showTick ? (
          <div className="dropdownTick">
            <svg className="icon mono">
              <use xlinkHref={selected ? "#tick" : "#blank"} />
            </svg>
          </div>
        ) : null}

        {renderOptionIcon(opt)}
        <div className="dropdownText">
          <div className="dropdownLabel">{getLabelWithUntitled(opt.label)}</div>
          {this.props.showDescription ? <div className={`dropdownDescription`}>{opt.description}</div> : null}
        </div>
      </div>
    );
  }

  renderHeaderOption(opt) {
    return (
      <div
        className={`dropdownMenuOptionHeader ${opt.disabled ? "disabled" : ""}`}
        key={opt.value}
        id={this.getListItemDomId(opt)}
      >
        <div className="dropdownText">
          <div className="dropdownLabel">{getLabelWithUntitled(opt.label)}</div>
        </div>
      </div>
    );
  }

  onMouseInOption(opt) {
    if (opt.children) {
      if (this.state.showingSubMenu !== opt.value) {
        this.setState({ showingSubMenu: opt.value });
      }
    } else if (this.state.showingSubMenu !== null) {
      this.setState({ showingSubMenu: null });
    }
  }

  renderParentOption(opt, isHighlighted, ref) {
    let style = {};
    if (isMobile) {
      style.paddingLeft = Math.max(this.props.paddingLeft, 5);
      style.paddingRight = Math.max(this.props.paddingRight, 5);
    }
    const selected = this.props.value === opt.value;
    const optClass = `dropdownMenuOption ${selected ? "selected" : ""} ${isHighlighted ? "highlighted" : ""} ${
      opt.disabled ? "disabled" : ""
    } ${this.props.showDescription ? "showDescription" : ""}`;

    const targetId = this.getListItemDomId(opt);

    return (
      <div
        ref={ref}
        className={optClass}
        style={style}
        key={opt.value}
        id={targetId}
        onMouseEnter={() => this.onMouseInOption(opt)}
        onClick={this.props.allowParentSelection ? () => this.onOptionClick(opt) : null}
      >
        {this.props.showTick ? (
          <div className="dropdownTick">
            <svg className="icon mono">
              <use xlinkHref={selected ? "#tick" : "#blank"} />
            </svg>
          </div>
        ) : null}
        {renderOptionIcon(opt)}
        <div className="dropdownText">
          <div className="dropdownLabel">{getLabelWithUntitled(opt.label)}</div>
          {this.props.showDescription ? <div className={`dropdownDescription`}>{opt.description}</div> : null}
        </div>
        <Icon icon="rightArrow" className="rightArrow" />
        {this.renderSubMenu(opt, targetId)}
      </div>
    );
  }

  optionRenderer(opt, isHighlighted, ref) {
    if (opt.header) {
      return this.renderHeaderOption(opt);
    } else if (opt.children) {
      return this.renderParentOption(opt, isHighlighted, ref);
    } else {
      return this.renderNormalOption(opt, isHighlighted, ref);
    }
  }

  onOptionClick(opt) {
    if (opt && !opt.disabled && (this.props.allowParentSelection || !opt.children)) {
      this.hideSubMenu();
      this.props.onOptionClick(opt.value);
    } else if (opt && !opt.disabled && opt.children) {
      this.setState({ showingSubMenu: opt.value });
    }
  }

  onSubOptionClick(value) {
    this.hideSubMenu();
    this.props.onOptionClick(value);
  }

  getListView() {
    const filteredOptions = getFilteredOptions(this.state.options, this.state.searchText);
    const selectedIndex = filteredOptions.findIndex(opt => opt.value === this.state.highlightValue);

    const onOptionSelect = newIndex => {
      const opt = filteredOptions[newIndex];
      this.onOptionClick(opt);
    };

    const onOptionHighlight = newIndex => {
      const opt = filteredOptions[newIndex];
      if (opt && !opt.disabled && opt.children) {
        this.setState({ showingSubMenu: opt.value });
      } else {
        this.hideSubMenu();
      }
    };

    return (
      <ListView
        className="dropdownMenuOptions"
        items={filteredOptions}
        selectedItemIndex={selectedIndex}
        onItemSelect={onOptionSelect}
        onItemHighlight={onOptionHighlight}
        itemRenderer={this.optionRenderer}
        getDom={listViewDom => (this._listViewDom = listViewDom)}
      />
    );
  }

  renderMenu() {
    if (this.props.showMenu && !this.props.disabled) {
      // Merge the modal heights
      let style = Object.assign({}, this.props.style || {});
      if (this.props.alwaysPortal) {
        style.maxHeight = this.props.maxHeight;
        style.height = this.props.fixedHeight;
      }

      return (
        <div
          style={style}
          className={`dropdownMenu ${this.props.allowSearch ? "" : "noSearch"} ${
            this.props.showDescription ? "showDescription" : ""
          } ${this.props.preserveSpaces ? "preserveSpaces" : ""}`}
        >
          {this.renderMenuSearch()}
          {this.getListView()}
        </div>
      );
    }
    return null;
  }

  hideSubMenu() {
    if (this.state.showingSubMenu) {
      this.setState({ showingSubMenu: null });
      this._listViewDom ? this._listViewDom.focus() : null;
    }
  }

  onToggleSubMenu(value) {
    if (!value) {
      this.hideSubMenu();
    }
  }

  getSubMenuDom(optValue, dom) {
    this._subMenuDomsMap = this._subMenuDomsMap.set(optValue, dom);
  }

  renderSubMenu(opt, targetId) {
    return (
      <FormDropdownMenu
        targetId={targetId}
        showMenu={opt.value === this.state.showingSubMenu}
        options={opt.children}
        onOptionClick={this.onSubOptionClick}
        onToggleMenu={this.onToggleSubMenu}
        allowSearch={false}
        allowParentSelection={this.props.allowParentSelection}
        value={this.props.value}
        popoverPlacement="right-start"
        popoverClassName={this.props.popoverClassName}
        title={opt.label}
        backText={this.props.title}
        showDescription={this.props.showDescription}
        style={this.props.doesChildrenFollowStyles ? this.props.style : null}
        disableTabLock={true}
        alwaysPortal={this.props.alwaysPortal}
        showTick={this.props.showTick}
        getDom={dom => this.getSubMenuDom(opt.value, dom)}
        focusOnOpen={false}
      />
    );
  }

  renderPopover() {
    let placement = this.props.popoverPlacement || "bottom-start";
    let hideArrow = true;
    let alwaysPortal = false;

    if (isTablet) {
      placement = "bottom-end";
      hideArrow = false;
      alwaysPortal = this.props.alwaysPortal;
    }

    if (isMobile && this.props.alwaysPortal !== false) {
      alwaysPortal = true;
    }

    let popoverOptions = {
      className: `dropdownPopover${isIos ? " iosModalContentWrap" : ""} ${
        this.props.popoverClassName ? this.props.popoverClassName : ""
      }`,
      placement,
      hideArrow,
      isOpen: this.props.showMenu,
      target: this.props.targetId,
      toggle: this.hideMenu
    };

    const willBePortal = alwaysPortal;

    const child = (
      <Fragment>
        {willBePortal || isMobile ? (
          <NavigationToolbar
            title={this.props.title}
            showBack={true}
            showBackArrow={true}
            onBackClick={this.toggle}
            backText={this.props.backText ? this.props.backText : "Back"}
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderMenu()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal, this.props.disableTabLock);
  }

  render() {
    return (
      <div className="dropdownMenuWrapper" ref={this._domNode} onKeyDown={this.onKeyDown}>
        {this.props.showMenu ? this.renderPopover() : null}
      </div>
    );
  }
}

FormDropdownMenu.propTypes = {
  targetId: PropTypes.string.isRequired,
  showMenu: PropTypes.bool.isRequired,
  options: PropTypes.oneOfType([PropTypeShapes.listOptionArrayOrList, PropTypes.func]).isRequired,
  onOptionClick: PropTypes.func.isRequired,
  allowSearch: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  disabled: PropTypes.bool,
  popoverPlacement: PropTypes.string,
  popoverClassName: PropTypes.string,
  onToggleMenu: PropTypes.func.isRequired,
  title: PropTypes.string,
  backText: PropTypes.string,
  showDescription: PropTypes.bool,
  useParentWidth: PropTypes.bool.isRequired,
  style: PropTypes.object,
  disableTabLock: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  getDom: PropTypes.func,
  preserveSpaces: PropTypes.bool,
  allowParentSelection: PropTypes.bool,
  doesChildrenFollowStyles: PropTypes.bool,

  //#!if iOS === true || harvard === true
  alwaysPortal: PropTypes.bool,
  showTick: PropTypes.bool,

  // From withScreenContext
  screenContext: PropTypes.object,

  // From withContainerContext
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,
  //#!endif

  // From ModalSizeContext
  maxHeight: PropTypes.number,
  fixedHeight: PropTypes.number
};

FormDropdownMenu.defaultProps = {
  focusOnOpen: true,
  useParentWidth: true,
  showTick: true
};

let exportedView = FormDropdownMenu;

//#!if iOS === true
// exportedView = withModalSizeContext(exportedView);
//#!endif

//#!if iOS === true || harvard === true
exportedView = withContainerContext(withScreenContext(exportedView));
//#!endif
export default exportedView;
